import type {DataRouterApplicationBuilder} from '@github-ui/react-core/future/data-router-application'
import {mainQuery} from '@github-ui/react-core/future/main-query'
import type {ShouldRevalidateFunctionArgs} from '@github-ui/react-router'
import {repoAppBuilder} from '@github-ui/repo-app-builder'
import type {AutomationHistoryPayload} from '../types/automation-revisions-payload'
import type {
  AutomationDetailPayload,
  AutomationRunsPayload,
  AutomationsPayload,
  EditAutomationPayload,
  NewAutomationPayload,
} from '../types/automations-payload'

function shouldRevalidateAutomationHistory({
  currentUrl,
  nextUrl,
  defaultShouldRevalidate,
  formMethod,
}: ShouldRevalidateFunctionArgs) {
  const currentSearchParams = new URLSearchParams(currentUrl.searchParams)
  const nextSearchParams = new URLSearchParams(nextUrl.searchParams)
  for (const param of ['history', 'revision']) {
    currentSearchParams.delete(param)
    nextSearchParams.delete(param)
  }

  if (
    formMethod === undefined &&
    currentUrl.pathname === nextUrl.pathname &&
    currentUrl.search !== nextUrl.search &&
    currentSearchParams.toString() === nextSearchParams.toString()
  ) {
    return false
  }

  return defaultShouldRevalidate
}

const historyNavigationOptions = {
  shouldRevalidate: shouldRevalidateAutomationHistory,
  uiServiceOptions: {
    tsrConfig: {
      // Search parameters omitted from queryDeps do not reload this route in the UI service.
      shouldReload: undefined,
    },
  },
} as const

/**
 * Automations list page route configuration.
 */
export function buildAutomationsRoute(appBuilder: DataRouterApplicationBuilder<string>) {
  return appBuilder.createQueryRouteConfig('automationsRoute', {
    path: '/:owner/:repo/agents/automations',
    queries: [mainQuery<AutomationsPayload>()],
  })
}

export const automationsRoute = buildAutomationsRoute(repoAppBuilder)

/**
 * Automation runs page route configuration (runs across all automations in the repo).
 */
export function buildAutomationRunsRoute(appBuilder: DataRouterApplicationBuilder<string>) {
  return appBuilder.createQueryRouteConfig('automationRunsRoute', {
    path: '/:owner/:repo/agents/automation-runs',
    queries: [mainQuery<AutomationRunsPayload>()],
  })
}

export const automationRunsRoute = buildAutomationRunsRoute(repoAppBuilder)

/**
 * New automation page route configuration.
 */
export function buildNewAutomationRoute(appBuilder: DataRouterApplicationBuilder<string>) {
  return appBuilder.createQueryRouteConfig('newAutomationRoute', {
    path: '/:owner/:repo/agents/automations/new',
    queries: [mainQuery<NewAutomationPayload>()],
  })
}

export const newAutomationRoute = buildNewAutomationRoute(repoAppBuilder)

/**
 * Individual automation detail page route configuration.
 */
export function buildAutomationDetailRoute(appBuilder: DataRouterApplicationBuilder<string>) {
  return appBuilder.createQueryRouteConfig('automationDetailRoute', {
    path: '/:owner/:repo/agents/automations/:automationID',
    queries: [mainQuery<AutomationDetailPayload>()],
    ...historyNavigationOptions,
  })
}

export const automationDetailRoute = buildAutomationDetailRoute(repoAppBuilder)

export function buildAutomationHistoryRoute(appBuilder: DataRouterApplicationBuilder<string>) {
  return appBuilder.createQueryRouteConfig('automationHistoryRoute', {
    path: '/:owner/:repo/agents/automations/:automationID/history',
    queries: [mainQuery<AutomationHistoryPayload>()],
    ...historyNavigationOptions,
  })
}

export const automationHistoryRoute = buildAutomationHistoryRoute(repoAppBuilder)

/**
 * Edit automation page route configuration.
 */
export function buildEditAutomationRoute(appBuilder: DataRouterApplicationBuilder<string>) {
  return appBuilder.createQueryRouteConfig('editAutomationRoute', {
    path: '/:owner/:repo/agents/automations/:automationID/edit',
    queries: [mainQuery<EditAutomationPayload>()],
  })
}

export const editAutomationRoute = buildEditAutomationRoute(repoAppBuilder)
